import _ from "lodash";
import React, { memo, useState, useRef, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { connect } from "react-redux";

import { useResize } from "Hooks";
import { isIdEqual } from "Utils";
import { compose } from "lib/apolloClient";
import {
  getDefaultNameSortPreference,
  getDefaultStudentNameOrderPreference,
  getUserId,
} from "modules/CommonSelectors";
import {
  CHAT_TYPE,
  MESSAGE_STATUS,
  MESSAGE_TYPE,
  getMessageDOMId,
  getFormattedNameForUserType,
  getIsMessagingWidget,
  getIsViewMode,
  getChatContainer,
  isMessagingRevempEnabled,
  isMessagingRevampV2Enabled,
  isMessagingReadReceiptEnabled,
  isCreateAndManageOwnChannelEnabled,
  isManageChannelsEnabledForNonCreator,
  canAdminDeleteMessage,
} from "MessagingV2/modules/Utils";
import { useChatOptions } from "MessagingV2/providers/ChatOptionsProvider";
import UnreadMessageLine from "MessagingV2/components/Messages/UnreadMessageLine";
import DateMessage from "MessagingV2/components/Messages/DateMessage";
import MessageBubble from "MessagingV2/components/Messages/MessageBubble";
import DeletedMessage from "MessagingV2/components/Messages/DeletedMessage";
import SummaryMessage from "MessagingV2/components/Messages/SummaryMessage";
import MessageReactions from "MessagingV2/components/Messages/MessageReactions";
import FailedMessageFooter from "MessagingV2/components/Messages/FailedMessageFooter";
import { getMessageStatus } from "MessagingV2/modules/MessagingV2Selector";
import MessageActivity from "MessagingV2/components/Messages/MessageActivity";
import MessageInteraction from "MessagingV2/components/Messages/MessageInteraction";
import ReadReceipt from "MessagingV2/components/Messages/ReadReceipt";

import {
  UserAvatarWithAccessPoint,
  UserName,
  AVATAR_RENDER_MODES,
} from "MessagingV2/components/UserAvatar";
import classes from "./MessageContainer.scss";
import MessageOptions from "../MessageOptions";

const updateMessageLabelForForwardedMessage = ({
  message,
  parentMessageDetails,
}) => {
  const { parentMessage = {} } = message;

  if (!_.isNil(parentMessageDetails)) {
    const {
      id,
      label,
      attachments = [],
      parentMessage: parentParentMessage = {},
    } = _.get(parentMessageDetails, "node", {});

    const isForwardedMessage = _.isEqual(
      _.get(parentParentMessage, "type"),
      MESSAGE_TYPE.FORWARDED_MESSAGE
    );

    if (isForwardedMessage) {
      const newLabel = label ? label : _.get(parentParentMessage, "label");
      const newAttachments =
        !label && !_.size(attachments)
          ? _.get(parentParentMessage, "attachments", [])
          : attachments;
      return {
        ...message,
        parentMessage: {
          ...message.parentMessage,
          label: newLabel,
          attachments: newAttachments,
        },
      };
    }
  }

  return message;
};

const Message = props => {
  const {
    message,
    chatType,
    openPreviewModal,
    isNextConsecutiveMessage,
    isSameDateAsPreviousMessage,
    isSameSenderAsPreviousMessage,
    isEmojiPickerOpenedForMessage,
    setMainEmojiPickerState,
    onEditMessageClick,
    onRetryMessageClick,
    onReplyMessageClick,
    onParentMessageClick,
    isMessageHighlighted,
    defaultNameSortPreference,
    getMessageElementIdCb,
    defaultStudentNameOrderPreference,
    isMessageOptionsVisible = true,
    status,
    isCurrentMessageSeperated,
    isNextMessageSeperated,
    isUserCreator,
    viewOnlyUsers,
    personalChatReceiverUserId,
  } = props;

  const [isIntersecting, setIsIntersecting] = useState(false);
  const ref = useRef(null);
  const {
    screenMode,
    activeUserId: userId,
    chatId,
    isPreviewMessagesModal,
  } = useChatOptions();

  useEffect(() => {
    const rootContainer = getChatContainer(chatId); // add root container for more accuracy and pass the chatId as dependency, as we are using here.
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsIntersecting(entry.isIntersecting);
      },
      {
        root: rootContainer,
      }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [chatId]);

  const [openedDropdown, setOpenedDropdown] = useState(null);
  const [bubbleContainerRef, dimensions] = useResize();

  const isMessagingWidget = getIsMessagingWidget({ screenMode });
  const isViewMode = getIsViewMode({ screenMode });
  const isUIRevempEnabled = isMessagingRevempEnabled();

  const {
    id: messageId,
    type,
    createdBy,
    reactions,
    isDeleted,
    isRead,
  } = message;
  const totalReactions = _.size(reactions);

  const isSender = isIdEqual(_.get(createdBy, "id"), userId);
  const isChannel = chatType === CHAT_TYPE.CHANNEL;

  const isBlocked = _.some(viewOnlyUsers, { id: createdBy.id });
  const isCurrentUserBlocked = _.some(viewOnlyUsers, { id: getUserId() });

  const canManageChannel =
    isChannel &&
    ((isUserCreator && isCreateAndManageOwnChannelEnabled()) ||
      (!isUserCreator && isManageChannelsEnabledForNonCreator()));

  const isUIRevampV2Enabled = isMessagingRevampV2Enabled();

  //in new UI we will always shows user avatar
  const isAvatarVisible = (isChannel && !isSender) || isUIRevempEnabled;

  const isConsecutiveMessagesFromUser =
    isSameSenderAsPreviousMessage && isSameDateAsPreviousMessage;

  const containerClasses = classNames(classes.container, {
    [classes.senderContainer]: isSender,
    [classes.consecutiveUserMessage]: isConsecutiveMessagesFromUser,
    [classes.nonConsecutiveUserMessage]: !isConsecutiveMessagesFromUser,
    [classes.fullScreenContainer]: !isMessagingWidget,
    [classes.widgetContainer]: isMessagingWidget,
  });

  const isFlaggedMessage = _.get(message, "isFlagged", false);
  const isMessageOptionsOpen =
    !_.isNull(openedDropdown) || isEmojiPickerOpenedForMessage;

  const widgetContainer = classNames({
    [classes.widgetContainer]: !isUIRevempEnabled,
    [classes.widgetContainerV2]: isUIRevempEnabled,
  });

  const containerClassV2 = classNames(classes.containerV2, {
    [classes.fullScreenContainer]: !isMessagingWidget,
    [widgetContainer]: isMessagingWidget,
    [classes.highlightedMessageContainer]:
      isMessageHighlighted && (!isViewMode || !isFlaggedMessage),
    [classes.highlightedFlaggedMessageContainer]:
      isMessageHighlighted && isViewMode && isFlaggedMessage,
    [classes.persistentBackground]: isMessageOptionsOpen,
    [classes.containerPaddingBottom]: isNextMessageSeperated,
    [classes.containerPaddingTop]: isCurrentMessageSeperated,
    [classes.fullScreenContainerWithRevampV2]: isUIRevampV2Enabled,
  });

  const contentClasses = classNames(classes.contentContainer, {
    [classes.fullScreenContentContainer]: !isMessagingWidget,
    // [classes.widgetContentContainer]: isMessagingWidget,
    [classes.senderContentContainer]: isSender,
    [classes.receiverContentContainer]: !isSender,
  });

  const channelActivityClasses = classNames({
    [classes.widgetContentContainer]: isMessagingWidget && !isUIRevempEnabled,
  });

  const optionContainerClasses = classNames(classes.optionsContainer, {
    [classes.senderOptionsContainer]: isSender,
    [classes.receiverOptionsContainer]: !isSender,
    [classes.persistentOptionsContainer]: isMessageOptionsOpen,
  });

  const optionContainerClassesV2 = classNames(classes.optionsContainerV2, {
    [classes.persistentOptionsContainer]: isMessageOptionsOpen,
  });

  const channelActivityContainerClass = classNames({
    [classes.channelActivityContainer]: !isUIRevempEnabled,
    [classes.channelActivityContainerV2]: isUIRevempEnabled,
    [classes.chanelActivityContainerRevampV2]: isUIRevampV2Enabled,
  });

  const handleRetryMessageClick = () => onRetryMessageClick(message);

  const fullName = getFormattedNameForUserType({
    user: createdBy,
    orderBy: defaultNameSortPreference,
    defaultStudentNameOrderPreference,
    isFormattedNameV2: true,
  });

  /* Failed or Sent Options */

  const showMessageOptions =
    isIntersecting &&
    status !== MESSAGE_STATUS.SENDING &&
    (!isViewMode || canAdminDeleteMessage()) &&
    isMessageOptionsVisible &&
    !isCurrentUserBlocked;

  const isSent = !status;

  const isReadReceipt =
    !isViewMode && !isPreviewMessagesModal && isMessagingReadReceiptEnabled();
  const getMessageElementId = _.isFunction(getMessageElementIdCb)
    ? getMessageElementIdCb
    : getMessageDOMId;

  switch (type) {
    case MESSAGE_TYPE.NORMAL:
      return (
        <div
          data-message={messageId}
          id={getMessageElementId(message)}
          ref={ref}
          className={isUIRevempEnabled ? containerClassV2 : containerClasses}
        >
          {isAvatarVisible ? (
            <UserAvatarWithAccessPoint
              user={createdBy}
              isBlocked={isBlocked}
              fullName={fullName}
              avatarRenderMode={
                isCurrentMessageSeperated
                  ? AVATAR_RENDER_MODES.AVATAR
                  : AVATAR_RENDER_MODES.SPACE
              }
            />
          ) : null}
          <div
            className={
              isUIRevempEnabled ? classes.contentContainerV2 : contentClasses
            }
          >
            {isAvatarVisible && isCurrentMessageSeperated ? (
              <div className={classes.nameReceiptContainer}>
                <UserName
                  user={createdBy}
                  fullName={fullName}
                  createdAt={message?.createdAt}
                />
                {status !== MESSAGE_STATUS.FAILED &&
                type !== MESSAGE_TYPE.CONVERSATION_SUMMARY &&
                isReadReceipt ? (
                  <div className={classes.readReceiptClass}>
                    <ReadReceipt
                      isSender={isSender}
                      isSent={isSent}
                      isRead={isRead}
                    />
                  </div>
                ) : null}
              </div>
            ) : null}
            {isDeleted ? (
              <DeletedMessage isSender={isSender} />
            ) : type === MESSAGE_TYPE.CONVERSATION_SUMMARY ? (
              <SummaryMessage message={message} />
            ) : (
              <>
                <div
                  ref={bubbleContainerRef}
                  className={
                    isUIRevempEnabled
                      ? classes.bubbleAndOptionsContainerV2
                      : classes.bubbleAndOptionsContainer
                  }
                >
                  <MessageBubble
                    status={status}
                    message={message}
                    isSender={isSender}
                    openPreviewModal={openPreviewModal}
                    isMessagingWidget={isMessagingWidget}
                    onParentMessageClick={onParentMessageClick}
                    isMessageHighlighted={isMessageHighlighted}
                  />

                  {showMessageOptions ? (
                    <div
                      className={
                        isUIRevempEnabled
                          ? optionContainerClassesV2
                          : optionContainerClasses
                      }
                    >
                      <MessageOptions
                        status={status}
                        message={message}
                        isSender={isSender}
                        isChannel={isChannel}
                        openedDropdown={openedDropdown}
                        setOpenedDropdown={setOpenedDropdown}
                        setMainEmojiPickerState={setMainEmojiPickerState}
                        onReplyMessageClick={onReplyMessageClick}
                        onEditMessageClick={onEditMessageClick}
                        getMessageElementId={getMessageElementId}
                        shouldSeparateMessage={isCurrentMessageSeperated}
                        sender={createdBy}
                        isBlocked={isBlocked}
                        canManageChannel={canManageChannel}
                        personalChatReceiverUserId={personalChatReceiverUserId}
                      />
                    </div>
                  ) : null}
                </div>

                {/* Failed or Sent Message Footer */}
                {status === MESSAGE_STATUS.FAILED ? (
                  <FailedMessageFooter
                    onRetryMessageClick={handleRetryMessageClick}
                  />
                ) : totalReactions ? (
                  <MessageReactions
                    message={message}
                    reactions={reactions}
                    width={dimensions.width}
                    setMainEmojiPickerState={setMainEmojiPickerState}
                    isNextConsecutiveMessage={isNextConsecutiveMessage}
                    disableReaction={!isMessageOptionsVisible}
                  />
                ) : null}
              </>
            )}
          </div>
        </div>
      );
    case MESSAGE_TYPE.CONVERSATION_SUMMARY: {
      return (
        <div
          id={getMessageElementId(message)}
          data-message={messageId}
          className={containerClassV2}
        >
          <SummaryMessage message={message} />
        </div>
      );
    }

    case MESSAGE_TYPE.INTERACTION: {
      return (
        <div
          data-message={messageId}
          id={getMessageElementId(message)}
          ref={ref}
          className={containerClassV2}
        >
          {isAvatarVisible ? (
            <UserAvatarWithAccessPoint
              isBlocked={isBlocked}
              user={createdBy}
              fullName={fullName}
              avatarRenderMode={
                isCurrentMessageSeperated
                  ? AVATAR_RENDER_MODES.AVATAR
                  : AVATAR_RENDER_MODES.SPACE
              }
            />
          ) : null}
          <div
            className={
              isUIRevempEnabled ? classes.contentContainerV2 : contentClasses
            }
          >
            {isAvatarVisible && isCurrentMessageSeperated ? (
              <div className={classes.nameReceiptContainer}>
                <UserName
                  user={createdBy}
                  fullName={fullName}
                  createdAt={message?.createdAt}
                />
                {status !== MESSAGE_STATUS.FAILED && isReadReceipt ? (
                  <div className={classes.readReceiptClass}>
                    <ReadReceipt
                      isSender={isSender}
                      isSent={isSent}
                      isRead={isRead}
                    />
                  </div>
                ) : null}
              </div>
            ) : null}

            <div
              ref={bubbleContainerRef}
              className={
                isUIRevempEnabled
                  ? classes.bubbleAndOptionsContainerV2
                  : classes.bubbleAndOptionsContainer
              }
            >
              <MessageInteraction message={message} />
              {showMessageOptions ? (
                <div
                  className={
                    isUIRevempEnabled
                      ? optionContainerClassesV2
                      : optionContainerClasses
                  }
                >
                  <MessageOptions
                    status={status}
                    message={message}
                    isSender={isSender}
                    isChannel={isChannel}
                    openedDropdown={openedDropdown}
                    setOpenedDropdown={setOpenedDropdown}
                    setMainEmojiPickerState={setMainEmojiPickerState}
                    onReplyMessageClick={onReplyMessageClick}
                    onEditMessageClick={onEditMessageClick}
                    getMessageElementId={getMessageElementId}
                    shouldSeparateMessage={isCurrentMessageSeperated}
                    isInteractiveMessage
                    isBlocked={isBlocked}
                    canManageChannel={canManageChannel}
                  />
                </div>
              ) : null}
            </div>

            {status === MESSAGE_STATUS.FAILED ? (
              <FailedMessageFooter
                onRetryMessageClick={handleRetryMessageClick}
              />
            ) : totalReactions ? (
              <MessageReactions
                message={message}
                reactions={reactions}
                width={dimensions.width}
                setMainEmojiPickerState={setMainEmojiPickerState}
                isNextConsecutiveMessage={isNextConsecutiveMessage}
                disableReaction={!isMessageOptionsVisible}
              />
            ) : null}
          </div>
        </div>
      );
    }
    case MESSAGE_TYPE.CHANNEL_ACTIVITY: {
      return (
        <div
          id={getMessageElementId(message)}
          data-message={messageId}
          className={channelActivityContainerClass}
        >
          <div className={channelActivityClasses}>
            <MessageActivity message={message} isBlocked={isBlocked} />
          </div>
        </div>
      );
    }
    default:
      return null;
  }
};

const CommonPropTypes = {
  openPreviewModal: PropTypes.func.isRequired,
  message: PropTypes.object.isRequired,
  isSameSenderAsPreviousMessage: PropTypes.bool.isRequired,
  chatType: PropTypes.oneOf(_.values(CHAT_TYPE)).isRequired,
  isNextConsecutiveMessage: PropTypes.bool.isRequired,
  setMainEmojiPickerState: PropTypes.func.isRequired,
  isEmojiPickerOpenedForMessage: PropTypes.bool.isRequired,
  onEditMessageClick: PropTypes.func.isRequired,
  onRetryMessageClick: PropTypes.func.isRequired,
  onReplyMessageClick: PropTypes.func.isRequired,
  onParentMessageClick: PropTypes.func.isRequired,
  isMessageHighlighted: PropTypes.bool.isRequired,
  isMessageOptionsVisible: PropTypes.bool,
  getMessageElementIdCb: PropTypes.func,
  status: PropTypes.oneOf(_.values(MESSAGE_STATUS)),
  isCurrentMessageSeperated: PropTypes.bool.isRequired,
  isNextMessageSeperated: PropTypes.bool.isRequired,
};

Message.propTypes = CommonPropTypes;

const MessageContainer = props => {
  const {
    isSameDateAsPreviousMessage,
    isUnreadLineVisible,
    message,
    unreadMessageCount,
    parentMessageDetails,
  } = props;

  //in case of replying to forwwarded message if label is null then we will find its parent message label
  //and attach it to current message parent message
  const updatedMessage = useMemo(() => {
    return updateMessageLabelForForwardedMessage({
      message,
      parentMessageDetails,
    });
  }, [message, parentMessageDetails]);

  const { createdAt, id } = message;

  return (
    <>
      {isUnreadLineVisible ? (
        <UnreadMessageLine
          messageId={id}
          unreadMessageCount={unreadMessageCount}
        />
      ) : null}
      {isSameDateAsPreviousMessage ? null : <DateMessage date={createdAt} />}
      <Message {...props} message={updatedMessage} />
    </>
  );
};

MessageContainer.propTypes = {
  ...CommonPropTypes,
  isSameDateAsPreviousMessage: PropTypes.bool.isRequired,
};

const mapStateToProps = (state, ownProps) => {
  const messageId = _.get(ownProps, "message.id", "");
  const defaultNameSortPreference = getDefaultNameSortPreference(state);
  const defaultStudentNameOrderPreference = getDefaultStudentNameOrderPreference(
    state
  );
  const status = getMessageStatus({ state, messageId });

  return {
    status,
    defaultNameSortPreference,
    defaultStudentNameOrderPreference,
  };
};

export default compose(connect(mapStateToProps), memo)(MessageContainer);
